import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "himmelstor_content";

const CONTENT_SYNC_URL = (process.env.REACT_APP_CONTENT_SYNC_URL || "").trim();
const CONTENT_SYNC_SECRET = (process.env.REACT_APP_CONTENT_SYNC_SECRET || "").trim();

const defaultContent = {
  home: {
    heroTitle: "Born to Farm",
    heroSubtext: "Himmelstor Farm is an integrated agricultural enterprise specializing in indigenous poultry, rabbit, goat, honey, pineapples, and vegetables. We offer consulting, farmer training, and agro-tourism experiences in Zimbabwe.",
    heroImage: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80",
    slideshow: [
      { src: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1200&q=80", caption: "Sustainable farming at Himmelstor" },
      { src: "https://images.unsplash.com/photo-1465101178521-c1a2b1c6413c?auto=format&fit=crop&w=1200&q=80", caption: "Fresh produce from our fields" },
      { src: "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=1200&q=80", caption: "Quality and care in everything we do" },
    ],
    card1Title: "Products",
    card1Text: "Indigenous poultry, rabbit, goat, honey, pineapples, vegetables.",
    card2Title: "Consulting",
    card2Text: "Agricultural consulting, farmer training, capacity building.",
    card3Title: "Agro-Tourism",
    card3Text: "Farm tours, educational visits, hands-on experiences.",
  },
  about: {
    title: "About Himmelstor Farm",
    intro: "Established in 2020 and registered in 2022, Himmelstor Farm is an integrated agricultural enterprise committed to food security, rural development, and sustainability. We specialize in indigenous poultry, rabbit, goat, honey, pineapples, and vegetables, using climate-smart and eco-friendly practices.",
    vision: "To be a leading model of sustainable agriculture and agro-tourism in Zimbabwe and beyond.",
    mission: "Empowering communities through innovative, sustainable farming and educational experiences.",
    values: ["Sustainability", "Integrity", "Innovation", "Community Impact", "Quality"],
    philosophy: "We focus on indigenous breeds, biodiversity, and organic practices to ensure healthy food and a thriving ecosystem.",
    founder: "Founded by Manu & Nyasha, our team brings together expertise in agriculture, business, and community development.",
    certifications: "(Placeholder for future certifications and partners.)",
  },
  products: {
    title: "Our Products",
    items: [
      { name: "Indigenous Poultry Meat", image: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80", description: "Naturally raised indigenous chickens, free-range, antibiotic-free.", availability: "Year-round", methods: "Sustainable, climate-smart practices." },
      { name: "Indigenous Poultry Eggs", image: "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80", description: "Fresh, nutritious eggs from indigenous breeds.", availability: "Year-round", methods: "Free-range, organic feed." },
      { name: "Rabbit Meat", image: "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?auto=format&fit=crop&w=800&q=80", description: "Lean, healthy rabbit meat from climate-smart production.", availability: "Seasonal", methods: "Low-chemical, humane practices." },
      { name: "Goat Meat (Chevon)", image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80", description: "Premium chevon from indigenous goats, raised sustainably.", availability: "Seasonal", methods: "Pasture-raised, organic supplements." },
      { name: "Natural Honey", image: "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=800&q=80", description: "Pure, raw honey from eco-friendly beekeeping.", availability: "Seasonal", methods: "Natural, chemical-free extraction." },
      { name: "Pineapples", image: "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=800&q=80", description: "Sweet, juicy pineapples grown with organic methods.", availability: "Seasonal", methods: "Organic, water-efficient farming." },
      { name: "Vegetables", image: "https://images.unsplash.com/photo-1465101178521-c1a2b1c6413c?auto=format&fit=crop&w=800&q=80", description: "Fresh, healthy vegetables from our fields.", availability: "Year-round", methods: "Low-chemical, sustainable practices." },
    ],
  },
  services: {
    title: "Our Services",
    items: [
      { name: "Agricultural Consulting", description: "Expert advice for farmers, startups, and institutions on livestock, crops, and agribusiness.", icon: "📋" },
      { name: "Farmer Training & Capacity Building", description: "Workshops and hands-on training to empower farmers and communities.", icon: "🎓" },
      { name: "Sustainable Farming Demonstrations", description: "On-site demonstrations of climate-smart, eco-friendly farming methods.", icon: "🌱" },
    ],
    advisoryAreas: ["Livestock", "Crops", "Agribusiness"],
    targetAudience: ["Farmers", "Startups", "Institutions"],
  },
  agroTourism: {
    title: "Agro-Tourism",
    activities: [
      { name: "Farm Tours", description: "Guided tours of our farm, livestock, and crops.", image: "https://images.unsplash.com/photo-1465101178521-c1a2b1c6413c?auto=format&fit=crop&w=800&q=80" },
      { name: "Educational Visits", description: "School and group visits for hands-on learning.", image: "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80" },
      { name: "Hands-on Farming Experiences", description: "Participate in daily farm activities and learn sustainable practices.", image: "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?auto=format&fit=crop&w=800&q=80" },
    ],
    bookTitle: "Book a Visit or Enquire",
    bookText: "Interested in a farm tour or group visit? Fill out our enquiry form and we'll respond within 24 hours.",
    galleryImages: [
      { src: "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=400&q=80", alt: "Beekeeping" },
      { src: "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=400&q=80", alt: "Pineapples" },
      { src: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80", alt: "Goats" },
      { src: "https://images.unsplash.com/photo-1465101178521-c1a2b1c6413c?auto=format&fit=crop&w=400&q=80", alt: "Farm" },
    ],
  },
  gallery: {
    title: "Gallery",
    images: [
      { src: "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=800&q=80", caption: "Beekeeping at Himmelstor Farm" },
      { src: "https://images.unsplash.com/photo-1502741338009-cac2772e18bc?auto=format&fit=crop&w=800&q=80", caption: "Pineapples in the field" },
      { src: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80", caption: "Indigenous goats grazing" },
      { src: "https://images.unsplash.com/photo-1465101178521-c1a2b1c6413c?auto=format&fit=crop&w=800&q=80", caption: "Farm landscape" },
      { src: "https://images.unsplash.com/photo-1519864600265-abb23847ef2c?auto=format&fit=crop&w=800&q=80", caption: "Rabbit farming" },
      { src: "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80", caption: "Egg collection" },
    ],
  },
  sustainability: {
    title: "Sustainability",
    items: [
      { title: "Climate-smart agriculture:", text: "Adapting to climate change with resilient practices." },
      { title: "Indigenous breeds & biodiversity:", text: "Preserving local breeds and promoting ecosystem health." },
      { title: "Organic / low-chemical practices:", text: "Minimizing chemical use for safer food and environment." },
      { title: "Water & soil conservation:", text: "Efficient water use and soil health management." },
      { title: "Community impact:", text: "Supporting rural development and food security." },
    ],
  },
  blog: {
    title: "News / Blog",
    posts: [
      { title: "Farming Updates: January 2026", excerpt: "Our indigenous poultry flock is thriving, and we've started a new batch of pineapples.", date: "2026-01-08", link: "#" },
      { title: "Upcoming Farmer Training Workshop", excerpt: "Join us for hands-on training in sustainable livestock and crop management.", date: "2026-01-15", link: "#" },
      { title: "Agro-Tourism Events This Season", excerpt: "Book your educational farm visit and experience nature-connected learning.", date: "2026-02-01", link: "#" },
    ],
  },
  contact: {
    title: "Contact Us",
    phone: "+263 719 133 497",
    email: "info@himmelstor.co.zw",
    businessHours: "Mon-Sat 8:00am - 5:00pm",
    mapEmbed: "",
  },
  enquiries: {
    title: "Enquiries",
    responseNote: "We aim to respond to all enquiries within 24 hours.",
  },
  footer: {
    title: "Himmelstor Farm",
    tagline: "Born to Farm. Integrated agricultural enterprise in Zimbabwe, specializing in indigenous poultry, rabbit, goat, honey, pineapples, and vegetables. Agro-tourism & consulting.",
    phone: "+263 719 133 497",
    email: "info@himmelstor.co.zw",
    companyReg: "Company Reg: Manu & Nyasha Investments Private Limited",
  },
  navbar: {
    brandName: "Himmelstor",
    brandHighlight: "Farm",
  },
};

function mergeSavedWithDefaults(saved) {
  if (!saved || typeof saved !== "object") return { ...defaultContent };
  const out = { ...defaultContent };
  for (const key of Object.keys(defaultContent)) {
    if (!(key in saved)) continue;
    const def = defaultContent[key];
    const sv = saved[key];
    if (
      sv &&
      typeof sv === "object" &&
      !Array.isArray(sv) &&
      def &&
      typeof def === "object" &&
      !Array.isArray(def)
    ) {
      out[key] = { ...def, ...sv };
    } else {
      out[key] = sv;
    }
  }
  return out;
}

function loadContent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return mergeSavedWithDefaults(parsed);
    }
  } catch (e) {}
  return { ...defaultContent };
}

function saveContent(content) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(content));
  } catch (e) {}
}

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [loadingInitialFromServer, setLoadingInitialFromServer] = useState(!!CONTENT_SYNC_URL);
  const [contentSyncWarning, setContentSyncWarning] = useState(null);

  const [content, setContentState] = useState(() => {
    if (CONTENT_SYNC_URL) {
      return mergeSavedWithDefaults({});
    }
    return loadContent();
  });

  useEffect(() => {
    if (!CONTENT_SYNC_URL) {
      setLoadingInitialFromServer(false);
      return;
    }

    let cancelled = false;

    fetch(CONTENT_SYNC_URL, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(String(res.status));
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled || !data || typeof data !== "object") return;
        if (Object.keys(data).length > 0) {
          setContentState(mergeSavedWithDefaults(data));
        } else {
          setContentState(mergeSavedWithDefaults({}));
        }
        setContentSyncWarning(null);
      })
      .catch(() => {
        if (cancelled) return;
        setContentState(loadContent());
        setContentSyncWarning("Warning: Could not sync with server. You may be seeing outdated data.");
      })
      .finally(() => {
        if (!cancelled) setLoadingInitialFromServer(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loadingInitialFromServer) return;
    saveContent(content);
  }, [content, loadingInitialFromServer]);

  const saveContentToServer = useCallback(async () => {
    saveContent(content);
    if (!CONTENT_SYNC_URL || !CONTENT_SYNC_SECRET) {
      throw new Error(
        "Server sync is not configured. Set REACT_APP_CONTENT_SYNC_URL and REACT_APP_CONTENT_SYNC_SECRET in .env.production, then run npm run build."
      );
    }
    const res = await fetch(CONTENT_SYNC_URL, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "X-Himmelstor-Key": CONTENT_SYNC_SECRET,
      },
      body: JSON.stringify(content),
    });
    let data = {};
    try {
      data = await res.json();
    } catch (_) {
      data = {};
    }
    if (res.status === 403) {
      console.warn(
        "Content sync returned 403 — ensure api/content-config.php secret matches REACT_APP_CONTENT_SYNC_SECRET in your build."
      );
      throw new Error(data.error || "Forbidden (403) — secret on server does not match this build.");
    }
    if (!res.ok) {
      throw new Error(data.error || `Save failed (${res.status})`);
    }
  }, [content]);

  const setSection = (section, data) => {
    setContentState((prev) => ({ ...prev, [section]: { ...prev[section], ...data } }));
  };

  const setContent = (newContent) => {
    setContentState(newContent);
  };

  const resetSection = (section) => {
    if (defaultContent[section]) {
      setContentState((prev) => ({ ...prev, [section]: defaultContent[section] }));
    }
  };

  const isContentLoading = !!CONTENT_SYNC_URL && loadingInitialFromServer;

  return (
    <ContentContext.Provider
      value={{
        content,
        setSection,
        setContent,
        resetSection,
        defaultContent,
        saveContentToServer,
        isContentLoading,
        contentSyncWarning,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
}
